<template>
  <v-dialog
    content-class="modern-dialog"
    v-model="dialog"
    persistent
    max-width="800px"
    z-index="1"
  >
    <v-card class="modern-card">
      <!-- Responsive Header -->
      <v-card-title class="dialog-header px-4 px-sm-6 pt-4 pt-sm-6 pb-3 pb-sm-4">
        <div class="header-title text-h5 text-sm-h5 font-weight-bold primary--text">
          Select Your Causes
        </div>
        <v-btn 
          icon 
          @click="onClose" 
          class="close-btn"
          :size="$vuetify.breakpoint.xs ? 'small' : 'default'"
        >
          <v-icon :size="$vuetify.breakpoint.xs ? '20' : '24'">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-4 px-sm-6 pb-2 text-body-2 text-sm-body-1 secondary--text">
        What types of causes are you passionate about?
      </v-card-text>

      <!-- Select All Button - Made more responsive -->
      <v-card-actions class="px-4 px-sm-6 pb-3 pb-sm-4">
        <v-btn
          @click="selectAll"
          outlined
          color="primary"
          class="select-btn rounded-pill"
          :class="{ 'all-selected': allSelected }"
          :x-small="$vuetify.breakpoint.xs"
          :small="$vuetify.breakpoint.sm"
        >
          <v-icon left :size="$vuetify.breakpoint.xs ? '18' : '24'">{{
            allSelected ? "mdi-heart" : "mdi-heart-outline"
          }}</v-icon>
          <span class="btn-text">
            {{ allSelected ? "All Selected" : "Select All" }}
          </span>
        </v-btn>
      </v-card-actions>

      <v-card-text class="px-2 px-sm-4 pb-0">
        <v-row>
          <v-col
            cols="6"
            sm="4"
            md="3"
            v-for="item in causes"
            :key="item.name"
            class="pa-1 pa-sm-2"
          >
            <v-hover v-slot="{ hover }">
              <v-card
                @click="selectCause(item)"
                :class="{
                  'selected-card': item.selected,
                  'hover-card': hover,
                  'elevated-card': item.selected || hover
                }"
                class="cause-card rounded-xl"
                :elevation="item.selected || hover ? 6 : 2"
              >
                <v-card-text
                  class="d-flex flex-column align-center justify-center py-3 py-sm-4 px-1 px-sm-2"
                >
                  <div class="icon-wrapper mb-2 mb-sm-3">
                    <site-icon
                      :name="item.name"
                      type="Cause"
                      :size="$vuetify.breakpoint.xs ? '32' : ($vuetify.breakpoint.sm ? '40' : '48')"
                      :color="item.selected ? 'primary' : 'secondary'"
                    />
                  </div>
                  <span
                    class="text-center font-weight-medium text-caption text-sm-body-2"
                    :class="item.selected ? 'primary--text' : 'secondary--text'"
                  >
                    {{ item.name }}
                  </span>
                </v-card-text>

                <!-- Heart icon for selected items -->
                <div class="heart-indicator" v-if="item.selected">
                  <v-icon color="primary" :size="$vuetify.breakpoint.xs ? '20' : '24'">mdi-heart</v-icon>
                </div>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="message" class="px-4 px-sm-6 py-0">
        <v-alert
          dense
          type="error"
          v-text="message"
          class="rounded-lg mt-3 mt-sm-4 mb-0 text-caption text-sm-body-2"
        />
      </v-card-text>

      <!-- Responsive Footer -->
      <v-card-actions class="dialog-footer px-4 px-sm-6 pb-4 pb-sm-6 pt-3 pt-sm-4">
        <v-btn
          color="#b5e539"
          @click="onSave"
          :width="$vuetify.breakpoint.xs ? '100%' : '200'"
          :height="$vuetify.breakpoint.xs ? '40' : '48'"
          :loading="loading"
          class="continue-btn rounded-pill"
          depressed
          :large="!$vuetify.breakpoint.xs"
        >
          <span class="font-weight-bold text-button text-sm-button">Continue</span>
          <v-icon right :size="$vuetify.breakpoint.xs ? '18' : '24'">mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import SiteIcon from "@/components/common/SiteIcon.vue";

export default {
  components: { SiteIcon },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      causes: []
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    ...mapGetters({
      allCauses: "icon/causes"
    }),
    allSelected() {
      return this.causes.length > 0 && this.causes.every(c => c.selected);
    }
  },
  methods: {
    ...mapActions({
      updateProfile: "auth/updateProfile",
      getCauseIcons: "icon/getCauseIcons"
    }),
    onChange() {
      this.message = null;
    },
    onSave() {
      const selectedCauses = this.causes
        .filter(c => c.selected)
        .map(c => c.name);
      if (selectedCauses.length === 0) {
        this.message = "Please select at least one cause to continue.";
        return;
      }

      this.loading = true;
      this.updateProfile({ causes: selectedCauses })
        .then(() => {
          this.loading = false;
          this.onClose();
        })
        .catch(error => {
          this.loading = false;
          this.message =
            error.response?.data?.message ||
            "An error occurred. Please try again.";
        });
    },
    selectCause(item) {
      item.selected = !item.selected;
      this.causes = [...this.causes];
      this.message = null;
    },
    selectAll() {
      const shouldSelect = !this.allSelected;
      this.causes.forEach(c => (c.selected = shouldSelect));
      this.causes = [...this.causes];
      this.message = null;
    }
  },
  watch: {
    allCauses(newValue) {
      this.causes = [...newValue].sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  mounted() {
    this.getCauseIcons().catch(error => console.log(error));
  }
};
</script>

<style scoped>
.modern-dialog {
  border-radius: 16px;
  overflow: hidden;
  z-index: 999999999 !important;
}

.modern-card {
  border-radius: 16px !important;
  background: linear-gradient(to bottom, #ffffff, #f8f9fa);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
}

.header-title {
  word-break: break-word;
  overflow-wrap: break-word;
  padding-right: 12px; /* Prevents text from touching close button */
}

.close-btn {
  transition: all 0.3s ease;
  flex-shrink: 0; /* Prevents button from shrinking on small screens */
}

.close-btn:hover {
  transform: rotate(90deg);
  color: var(--v-error-base) !important;
}

.cause-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 2px solid transparent;
  position: relative;
}

.cause-card:hover {
  transform: translateY(-4px);
}

.selected-card {
    border-color: #3a7bd5 !important;
  background-color: rgba(10, 91, 138, 0.05) !important;
}

.hover-card {
     border-color: #3a7bd5 !important;
}

.icon-wrapper {
  position: relative;
}

.heart-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  background: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.select-btn {
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}

.select-btn.all-selected {
  background-color: rgba(10, 91, 138, 0.1) !important;
}

.dialog-footer {
  display: flex;
  justify-content: center;
}

.continue-btn {
  background-color: #b5e539 !important;
  box-shadow: 0 4px 12px rgba(181, 229, 57, 0.3);
  transition: all 0.3s ease;
  min-width: 0; /* Allows button to shrink on mobile */
}

.continue-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(181, 229, 57, 0.4);
}

.continue-btn:active {
  transform: translateY(0);
}

/* Mobile-specific adjustments */
@media (max-width: 600px) {
  .modern-dialog {
    margin: 8px;
  }
  
  .dialog-header {
    padding-top: 16px !important;
    padding-bottom: 12px !important;
  }
  
  .header-title {
    font-size: 1.25rem !important;
    line-height: 1.5;
  }
  
  .btn-text {
    font-size: 0.8rem;
  }
  
  .cause-card {
    padding: 4px !important;
  }

  .heart-indicator {
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
  }
  
  .dialog-footer {
    padding-top: 16px !important;
    padding-bottom: 20px !important;
  }
}

/* Extra small devices */
@media (max-width: 400px) {
  .header-title {
    font-size: 1.1rem !important;
  }
  
  .select-btn {
    padding-left: 8px;
    padding-right: 8px;
  }
}

/* Custom scrollbar for dialog */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: var(--v-primary-lighten1);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--v-primary-base);
}
</style>